import { apiClient } from "../../../services/api";
import { ClientError } from "../../../../../api/src/shared/request";
import { extractRoomIdFromPath } from "../../room/utils/roomIdFromPath";
import { isRoomContentPath } from "../../room/utils/roomContentPath";
import { getRoomPathname } from "../../room/utils/getRoomPathname";
import { claimAnonymousConversations } from "../../gpt/anonymous/anonymousGpt";
import { GPT_LANDING_PATH } from "@juisci/gpt-handoff";

/**
 * Where a reader lands once they have signed in or finished onboarding, given
 * the path they were trying to open before the app asked them to.
 *
 * Most paths are returned as they are. A room page is the exception, because
 * the reader may not be allowed into it:
 *
 *  - a member gets the page they asked for;
 *  - a public or discoverable room opens its home page (`getRoomPathname`),
 *    which shows the Join button to an eligible non-member;
 *  - a private client space is for its members, so a non-member goes to `/`,
 *    their own home.
 *
 * A piece of room content (`isRoomContentPath`) opens for everyone, so it is
 * returned as it is, like any other shared page.
 *
 * The JuisciGPT landing is the logged-out page's way back
 * (`returnToJuisciGptAfterSignIn`): it opens the newest conversation the
 * visitor had before signing in, now claimed into the account, and stays the
 * landing when there is none.
 */
export async function resolvePostAuthDestination(
  path: string,
  user: { readonly id: string; readonly rooms: readonly string[] }
): Promise<string> {
  if (path === GPT_LANDING_PATH) {
    const [newest] = await claimAnonymousConversations(user.id);
    return newest === undefined ? path : `/gpt/chat/conversation/${newest}`;
  }
  const roomId = roomNeedingAccessCheck(path, user.rooms);
  if (roomId === null) return path;
  try {
    const { body: room } = await apiClient.get("/webapp/public/rooms/:roomId", {
      param: { roomId },
      query: {},
    });
    return nonMemberRoomDestination(roomId, room);
  } catch (error) {
    // A room that does not exist sends the reader home. Any other failure
    // leaves the room's kind unknown, so the path opens as it is and the room
    // guard answers there. `onRequestError` has already reported it.
    if (error instanceof ClientError && error.responseStatus === 404) {
      return "/";
    }
    return path;
  }
}

/**
 * The room whose kind decides the destination: a room page, not a piece of
 * content in it, that the reader is not a member of. Null for every other path.
 */
export function roomNeedingAccessCheck(
  path: string,
  userRooms: readonly string[]
): string | null {
  const { pathname } = new URL(path, globalThis.location.origin);
  const roomId = extractRoomIdFromPath(pathname);
  if (roomId === null) return null;
  if (isRoomContentPath(pathname)) return null;
  if (userRooms.includes(roomId)) return null;
  return roomId;
}

/** `room` is the public room card. */
export function nonMemberRoomDestination(
  roomId: string,
  room: {
    readonly isPublic: boolean;
    readonly discoverable: boolean;
  }
): string {
  if (room.isPublic || room.discoverable) {
    return getRoomPathname(roomId);
  }
  return "/";
}
